import dayjs from 'dayjs';

import { api } from '$shared/api/index';
import type { MatchRelevance } from '$shared/constants/MatchRelevance';
import type { IMonitoringConfig } from '$shared/interfaces/api/IMonitoringConfig';
import { appPermissionData } from '$shared/lib/data/AppPermissionData';
import type OrgSettings from '$shared/models/OrgSettings';
import BaseService from '$shared/lib/services/BaseService';
import { appData } from '$shared/lib/appData';
import { GlobalAppDataKey } from '$shared/constants/GlobalAppDataKey';
import { OrgPermission } from '$shared/constants/OrgPermission';
import { schemaData } from '$shared/lib/data/SchemaData';
import ProviderProfileSource from '$provider/models/ProviderProfileSource';
import { VerificationData } from '../appData/VerificationData';
import { UIError } from '$shared/models/UIError';

const SettingsRefreshSyncInterval = 2; // Hours

class SettingsService extends BaseService {
  private lastSyncTime = dayjs();

  get data(): OrgSettings | undefined {
    return appData.getData<OrgSettings>(GlobalAppDataKey.OrgSettings);
  }

  get credentialingStatuses(): Array<string> {
    return this.data?.CredentialingStatuses || [];
  }

  get isDeleteProviderDisabled(): boolean {
    return this.data?.DisableDeleteProvider || false;
  }

  get matchRelevancePreference(): MatchRelevance | undefined {
    return this.data?.MatchRelevancePreference;
  }

  // 2DO Once the match relevance is no longer feature then we should remove
  // this property here and wherever we used
  get hasMatchRelevancePreferenceAvailable(): boolean {
    return !!this.matchRelevancePreference;
  }

  get isCvoEnabled(): boolean {
    return !!this.data?.EnableCredentialingRequestApi;
  }

  get hasNpdbEnabledConfig(): boolean {
    return !!this.data?.NpdbConfiguration;
  }

  get haveAnyNpdbConfigExist(): boolean {
    return (
      Object.entries(this.data?.NpdbConfigurationSegments || {}).length > 0 ||
      this.hasNpdbEnabledConfig
    );
  }

  get isNpdbEnabled(): boolean {
    return this.haveAnyNpdbConfigExist && this.enabledDatasets.includes(schemaData.Dataset.Npdb);
  }

  get isNscEnabled(): boolean {
    return this.enabledDatasets.includes(schemaData.Dataset.Nsc);
  }

  get isNpiEnabled(): boolean {
    return !!this.enabledDatasets.includes(schemaData.Dataset.Npi);
  }

  public get hasPreventMultipleAddressesAndEmails(): boolean {
    return !!this.data?.PreventMultipleHomeWorkAddressesAndEmails;
  }

  public get enableMonitoringDashboard(): boolean {
    return !!this.data?.EnableMonitoringDashboard;
  }

  public get hasAnyImportEnabled(): boolean {
    return this.hasAnyImportEnabledForCaqh || this.hasAmaProfilesEnabled;
  }

  public get isFacilityEnabled(): boolean {
    return !!this.data?.CvoFacilityFeatures?.enrolledInCvoServices;
  }

  public get isCvoServicesEnabled(): boolean {
    return !!this.data?.CvoFeatures?.enrolledInCvoServices;
  }

  public get isProfileMergesEnabled(): boolean {
    return !!this.data?.EnableProfileMerges;
  }

  get isDatasetsEnabled(): boolean {
    return !!this.data?.EnableDatasets;
  }

  get isSanctionsEnabled(): boolean {
    const sanctionDataset: string[] = this.enabledSanctionDatasets;
    return this.enabledDatasets && sanctionDataset.length !== 0;
  }

  get whitelistedDatasets(): string[] {
    if (this.data?.EnableDatasetsWhitelist.length === 0) {
      return [];
    }
    return [...(this.data?.EnableDatasetsWhitelist || [])];
  }

  get enabledSanctionDatasets(): string[] {
    return this.enabledDatasets.filter((dataset: string) =>
      VerificationData.data?.SanctionDataset.includes(dataset)
    );
  }

  get enabledNonSanctionDatasets(): string[] {
    return this.enabledDatasets.filter(
      (dataset: string) => !VerificationData.data?.SanctionDataset.includes(dataset)
    );
  }

  get enabledDatasetMatches(): string[] {
    const enabledSanctions = this.enabledSanctionDatasets;
    const enabledNonSanctions = this.enabledNonSanctionDatasets.filter(
      (dataset) => dataset === schemaData.Dataset.Npdb
    );
    return [...enabledSanctions, ...enabledNonSanctions];
  }

  get enabledDatasets(): string[] {
    if (!this.isDatasetsEnabled) {
      return [];
    }

    let datasets: string[] = Object.values(schemaData.Dataset);

    if (this.whitelistedDatasets?.length > 0) {
      datasets = this.whitelistedDatasets.filter((dataset) => !!schemaData.Dataset[dataset]);
    }

    if (!this.haveAnyNpdbConfigExist) {
      datasets = datasets.filter((type) => type !== schemaData.Dataset.Npdb);
    }

    return datasets.filter((dataset) => !!VerificationData.data?.getDatasetMetadata(dataset));
  }

  get hasBetaAccess(): boolean {
    return !!this.data?.BetaAccess;
  }

  get organizationName(): string {
    return this.data?.organizationName?.trim() || '';
  }

  getEnabledSanctionDatasets(includeNonSanctions?: string[]): Array<string> {
    const datasets = this.enabledSanctionDatasets;

    includeNonSanctions?.forEach((dataset) => {
      const datasetKey = this.enabledNonSanctionDatasets.find((item) => item === dataset);
      if (datasetKey) {
        datasets.push(datasetKey);
      }
    });
    return datasets;
  }

  getDatasetMonitoringConfig(dataset: string): IMonitoringConfig {
    return (
      this.data?.DatasetMonitoringSettings[dataset] || {
        enableMonitoring: false,
        monitoringInterval: schemaData.MonitoringInterval.Monthly
      }
    );
  }

  getLicenseMonitoringConfig(): IMonitoringConfig {
    return (
      this.data?.LicenseMonitoringSettings || {
        enableMonitoring: false,
        monitoringInterval: schemaData.MonitoringInterval.Monthly
      }
    );
  }

  public getCredentialingStatusesAsList(): CommonList[] {
    return this.credentialingStatuses.map(
      (status: string): CommonList => ({
        id: status,
        label: status,
        active: false
      })
    );
  }

  public get hasAnyMedicaidDatasetsEnabled(): boolean {
    return !!VerificationData.data?.medicaidDatasets.some((metadata) =>
      this.checkDatasetIsEnable(metadata.type)
    );
  }

  public get hasAmaProfilesEnabled(): boolean {
    return [
      ProviderProfileSource.AmericanMedicalAssociationPhysician,
      ProviderProfileSource.AmericanMedicalAssociationPhysicianAssistant
    ].some((source: ProviderProfileSource) => this.checkProfileSourceEnabled(source));
  }

  public ensureDatasetMonitoringEnabled(dataset: string): boolean {
    return !!this.data?.DatasetMonitoringSettings[dataset]?.enableMonitoring;
  }

  public checkProfileSourceEnabled(source: ProviderProfileSource): boolean {
    try {
      this.ensureProfileSourceSettingEnabled(source);
      return true;
    } catch {
      return false;
    }
  }

  public ensureProfileSourceSettingEnabled(source: ProviderProfileSource): void {
    const isEnabled = !!this.data?.ProviderProfileImportSourceWhiteList?.includes(source);
    if (!isEnabled)
      throw new UIError(`${source} profile source is not enabled for your organization`);
  }

  public checkDatasetIsEnable(dataset: string): boolean {
    return this.enabledDatasets.includes(dataset);
  }

  public checkGlobalDatasetIsEnable(dataset: string): boolean {
    const datasets = this.data?.getAllEnabledDatasets();
    return !!datasets?.includes(dataset);
  }

  public async loadSettingsAsync(): Promise<void> {
    const orgSettings = await api.getOrgSettingsAsync();
    appData.setData(GlobalAppDataKey.OrgSettings, orgSettings);

    appPermissionData.setViewAccess(
      OrgPermission.MedicaidEnrollment,
      this.hasAnyMedicaidDatasetsEnabled
    );
    this.lastSyncTime = dayjs();
  }

  public get enableDeactivatingProvider(): boolean {
    const value = this.data?.EnableDeactivatingProvider;
    return typeof value !== 'boolean' ? true : !!value;
  }

  public get isCaqhPoEnabled(): boolean {
    return this.checkProfileSourceEnabled(ProviderProfileSource.CaqhPo);
  }

  public get hasAnyImportEnabledForCaqh(): boolean {
    return this.isCaqhPoEnabled || this.checkProfileSourceEnabled(ProviderProfileSource.Caqh);
  }

  public async refreshSettingsAsync(): Promise<void> {
    const isValidSyncTime = dayjs().diff(this.lastSyncTime, 'hours') > SettingsRefreshSyncInterval;
    if (!isValidSyncTime) return;

    return this.loadSettingsAsync();
  }

  override onUserUnauthorized(): void {
    this.reset();
  }

  override onUserOrgChanged(): void {
    this.reset();
  }

  public reset(): void {
    appData.setData(GlobalAppDataKey.OrgSettings, undefined);
  }
}

export default new SettingsService();
